import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { SetupDirectionSection } from "../components/SetupDirectionSection";
import { ArrowRight, ArrowLeft, Check, User as UserIcon, Sparkles, Plus, Trash2, MessageCircle, BookOpen, PenLine } from "lucide-react";

const STEPS = ["Protagonists", "Theme", "Vibes", "Title & Tone", "Begin"];

const VIBE_OPTIONS = [
  { v: "romantic",    l: "Romantic",    d: "hearts on sleeves" },
  { v: "tender",      l: "Tender",      d: "soft, unguarded" },
  { v: "hopeful",     l: "Hopeful",     d: "warmth, recovery" },
  { v: "whimsical",   l: "Whimsical",   d: "lighthearted, playful" },
  { v: "witty",       l: "Witty",       d: "clever banter, sharp" },
  { v: "playful",     l: "Playful",     d: "teasing, mischief" },
  { v: "adventurous", l: "Adventurous", d: "journeys, wonder" },
  { v: "nostalgic",   l: "Nostalgic",   d: "memory, old familiarity" },
  { v: "bittersweet", l: "Bittersweet", d: "loss-tinged tenderness" },
  { v: "melancholy",  l: "Melancholy",  d: "ache, rain-stained" },
  { v: "tense",       l: "Tense",       d: "held breath, stakes" },
  { v: "dangerous",   l: "Dangerous",   d: "risk, transgression" },
  { v: "dark",        l: "Dark",        d: "shadowed, deeper pulls" },
  { v: "sensual",     l: "Sensual",     d: "slow touch, charged air" },
  { v: "sexy",        l: "Sexy",        d: "bodies, appetites" },
  { v: "hot",         l: "Hot",         d: "unmistakable heat" },
];

const THEME_SUGGESTIONS = [
  { name: "Last Train", description: "The final train of the night pulls in with just the two of them on the platform.", default_vibes: ["tense", "romantic"] },
  { name: "The Stowaway", description: "She boards the boat for somewhere warm; she isn't supposed to be there.", default_vibes: ["dangerous", "sensual"] },
  { name: "Writers' Retreat", description: "A remote cabin. Two drafts due Monday. Neither is getting written.", default_vibes: ["playful", "sensual", "tender"] },
  { name: "Border Crossing", description: "The checkpoint officer takes a long time with her passport.", default_vibes: ["tense", "dangerous"] },
  { name: "Summer Estate", description: "Heat, citrus, old rivalries, a bedroom door left unlocked.", default_vibes: ["sensual", "hot", "dangerous"] },
  { name: "The Rainstorm", description: "A sudden downpour traps them under the same awning, then indoors.", default_vibes: ["romantic", "tender"] },
];

const CreateStory = () => {
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [characters, setCharacters] = useState([]);
  const [themes, setThemes] = useState([]);
  const [mode, setMode] = useState("prose"); // "prose" | "chat"
  const [selectedCharIds, setSelectedCharIds] = useState([]);
  const [controlledIds, setControlledIds] = useState([]);
  const [themeId, setThemeId] = useState("");
  const [vibes, setVibes] = useState([]);
  const [customVibeInput, setCustomVibeInput] = useState("");
  const [title, setTitle] = useState("");
  const [intensity, setIntensity] = useState("steamy");
  const [genre, setGenre] = useState("open");
  const [setupNote, setSetupNote] = useState("");
  const [avoidNote, setAvoidNote] = useState("");
  const [noEmotionalArc, setNoEmotionalArc] = useState(false);
  const [walkOns, setWalkOns] = useState(true);
  const [directions, setDirections] = useState({});
  const [generating, setGenerating] = useState(false);
  const [chatReplyMode, setChatReplyMode] = useState("auto"); // "auto" | "directed"
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const [themeForm, setThemeForm] = useState({ name: "", description: "", default_vibes: [] });
  const [savingTheme, setSavingTheme] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [c, t] = await Promise.all([api.get("/characters"), api.get("/themes")]);
        setCharacters(c.data);
        setThemes(t.data);
      } catch { toast.error("Could not load characters"); }
    })();
  }, []);

  const toggleChar = (id) => {
    setSelectedCharIds((prev) => {
      if (prev.includes(id)) {
        setControlledIds(ctrl => ctrl.filter(x => x !== id));
        return prev.filter(x => x !== id);
      }
      const cap = (mode === "chat" || mode === "scene") ? 4 : 5;
      if (prev.length >= cap) return prev;
      return [...prev, id];
    });
  };

  const toggleControl = (id, e) => {
    e?.stopPropagation();
    setControlledIds((prev) => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      // Chat/Scene mode: only one player allowed — replace the existing selection
      if (mode === "chat" || mode === "scene") return [id];
      return [...prev, id];
    });
  };

  const pickMode = (m) => {
    setMode(m);
    if (m === "chat" || m === "scene") {
      // Chat & Scene modes: 2-4 characters and exactly one player
      setSelectedCharIds(prev => prev.slice(0, 4));
      setControlledIds(prev => prev.slice(0, 1));
    }
  };

  const pickTheme = (theme) => {
    setThemeId(theme.id);
    const defaults = (theme.default_vibes || []).slice(0, 6);
    if (vibes.length === 0) setVibes(defaults);
  };

  const toggleVibe = (v) => {
    setVibes((prev) => {
      if (prev.includes(v)) return prev.filter(x => x !== v);
      if (prev.length >= 6) return prev;
      return [...prev, v];
    });
  };

  const addCustomVibe = () => {
    const v = customVibeInput.trim().toLowerCase();
    if (!v) return;
    if (vibes.includes(v)) { setCustomVibeInput(""); return; }
    if (vibes.length >= 6) { toast.error("Max 6 vibes"); return; }
    setVibes(prev => [...prev, v]);
    setCustomVibeInput("");
  };

  const openThemeDialog = () => {
    setThemeForm({ name: "", description: "", default_vibes: [] });
    setThemeDialogOpen(true);
  };

  const applySuggestion = (s) => {
    setThemeForm({ name: s.name, description: s.description, default_vibes: [...s.default_vibes] });
  };

  const toggleThemeFormVibe = (v) => {
    setThemeForm(f => {
      const has = (f.default_vibes || []).includes(v);
      if (has) return { ...f, default_vibes: f.default_vibes.filter(x => x !== v) };
      if ((f.default_vibes || []).length >= 6) return f;
      return { ...f, default_vibes: [...(f.default_vibes || []), v] };
    });
  };

  const saveCustomTheme = async () => {
    if (!themeForm.name.trim() || !themeForm.description.trim()) {
      toast.error("Name and description are required");
      return;
    }
    setSavingTheme(true);
    try {
      const r = await api.post("/themes", {
        name: themeForm.name.trim(),
        description: themeForm.description.trim(),
        default_vibes: themeForm.default_vibes,
      });
      setThemes(prev => [...prev, r.data]);
      pickTheme(r.data);
      setThemeDialogOpen(false);
      toast.success("Theme saved.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save theme");
    } finally { setSavingTheme(false); }
  };

  const canAdvance = () => {
    if (step === 0) {
      if (mode === "chat" || mode === "scene") {
        return selectedCharIds.length >= 2 && selectedCharIds.length <= 4 && controlledIds.length === 1;
      }
      return selectedCharIds.length >= 1;
    }
    if (step === 1) return !!themeId;
    if (step === 2) return true;
    if (step === 3) return title.trim().length > 0;
    return true;
  };

  const submit = async () => {
    setGenerating(true);
    try {
      const payload = {
        title: title.trim(),
        character_ids: selectedCharIds,
        controlled_character_ids: controlledIds,
        theme_id: themeId,
        intensity,
        vibes,
        setup_note: setupNote.trim(),
        avoid_note: avoidNote.trim(),
        no_emotional_arc: noEmotionalArc,
        genre,
        mode,
        walk_ons_enabled: mode === "scene" ? walkOns : true,
        character_direction: Object.fromEntries(
          Object.entries(directions).filter(([cid]) => selectedCharIds.includes(cid) && !controlledIds.includes(cid))
        ),
      };
      if (mode === "chat") {
        payload.chat_player_id = controlledIds[0];
        payload.chat_reply_mode = chatReplyMode;
      }
      if (mode === "scene") {
        payload.chat_player_id = controlledIds[0];
        payload.chat_reply_mode = chatReplyMode;
      }
      const r = await api.post("/stories", payload);
      navigate(`/story/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create story");
      setGenerating(false);
    }
  };

  const selectedChars = selectedCharIds.map(id => characters.find(c => c.id === id)).filter(Boolean);
  const selectedTheme = themes.find(t => t.id === themeId);

  return (
    <div className="min-h-screen pt-24 pb-20 bg-stone-950 grain-overlay" data-testid="create-story-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="mb-14">
          <div className="overline text-xs font-sans-ui uppercase tracking-[0.3em] text-stone-500 mb-3">
            Chapter {["I","II","III","IV","V"][step]}
          </div>
          <h1 className="font-serif-heading text-4xl sm:text-5xl md:text-6xl font-light text-stone-100 leading-none">
            {STEPS[step]}
          </h1>
        </div>

        {/* Step 0: Characters + control */}
        {step === 0 && (
          <div className="space-y-10 fade-up">
            {(user?.chat_mode_enabled || user?.scene_mode_enabled) && (
              <div data-testid="format-picker" className="border border-stone-800 bg-stone-900/30 p-5">
                <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-4">
                  Format
                </div>
                <div className={`grid gap-3 ${(user?.chat_mode_enabled && user?.scene_mode_enabled) ? "grid-cols-3" : "grid-cols-2"}`}>
                  <button
                    type="button"
                    data-testid="format-prose"
                    onClick={() => pickMode("prose")}
                    className={`p-5 border text-left transition ${mode === "prose"
                      ? "border-rose-800 bg-rose-950/25"
                      : "border-stone-800 bg-stone-900/40 hover:border-stone-600"}`}
                  >
                    <BookOpen className="w-4 h-4 mb-2 text-rose-400" strokeWidth={1.5} />
                    <div className="font-serif-heading text-xl text-stone-100 mb-1">Prose</div>
                    <div className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-stone-500">
                      Traditional chaptered story
                    </div>
                  </button>
                  {user?.scene_mode_enabled && (
                  <button
                    type="button"
                    data-testid="format-scene"
                    onClick={() => pickMode("scene")}
                    className={`p-5 border text-left transition ${mode === "scene"
                      ? "border-rose-800 bg-rose-950/25"
                      : "border-stone-800 bg-stone-900/40 hover:border-stone-600"}`}
                  >
                    <PenLine className="w-4 h-4 mb-2 text-rose-400" strokeWidth={1.5} />
                    <div className="font-serif-heading text-xl text-stone-100 mb-1">Scene</div>
                    <div className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-stone-500">
                      Screenplay format — a few lines at a time
                    </div>
                  </button>
                  )}
                  {user?.chat_mode_enabled && (
                  <button
                    type="button"
                    data-testid="format-chat"
                    onClick={() => pickMode("chat")}
                    className={`p-5 border text-left transition ${mode === "chat"
                      ? "border-rose-800 bg-rose-950/25"
                      : "border-stone-800 bg-stone-900/40 hover:border-stone-600"}`}
                  >
                    <MessageCircle className="w-4 h-4 mb-2 text-rose-400" strokeWidth={1.5} />
                    <div className="font-serif-heading text-xl text-stone-100 mb-1">Texting</div>
                    <div className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-stone-500">
                      Live text-thread back-and-forth
                    </div>
                  </button>
                  )}
                </div>
                {mode === "chat" && (
                  <div className="mt-5 space-y-4">
                    <p className="font-serif-reading italic text-stone-500 text-sm leading-relaxed">
                      Pick <span className="text-rose-300">two to four</span> characters, then tap{" "}
                      <span className="text-rose-300">&ldquo;I play as them&rdquo;</span> on the one you&rsquo;ll text as.
                    </p>
                    <div data-testid="reply-mode-picker" className="border border-stone-800 bg-stone-900/30 p-4">
                      <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-3">
                        Who replies?
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          data-testid="reply-mode-auto"
                          onClick={() => setChatReplyMode("auto")}
                          className={`p-3 border text-left transition ${chatReplyMode === "auto"
                            ? "border-rose-800 bg-rose-950/25"
                            : "border-stone-800 bg-stone-900/40 hover:border-stone-600"}`}
                        >
                          <div className="font-serif-reading text-stone-100 mb-0.5">Natural</div>
                          <div className="font-sans-ui text-[9px] uppercase tracking-[0.2em] text-stone-500">
                            AI decides who chimes in
                          </div>
                        </button>
                        <button
                          type="button"
                          data-testid="reply-mode-directed"
                          onClick={() => setChatReplyMode("directed")}
                          className={`p-3 border text-left transition ${chatReplyMode === "directed"
                            ? "border-rose-800 bg-rose-950/25"
                            : "border-stone-800 bg-stone-900/40 hover:border-stone-600"}`}
                        >
                          <div className="font-serif-reading text-stone-100 mb-0.5">Directed</div>
                          <div className="font-sans-ui text-[9px] uppercase tracking-[0.2em] text-stone-500">
                            You pick who replies, in order
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {mode === "scene" && (
                  <div className="mt-5 space-y-4">
                    <p className="font-serif-reading italic text-stone-500 text-sm leading-relaxed">
                      Pick <span className="text-rose-300">two to four</span> characters, then tap{" "}
                      <span className="text-rose-300">&ldquo;I play as them&rdquo;</span> on the one whose lines you&rsquo;ll write.
                    </p>
                    {selectedCharIds.length >= 3 && (
                      <div data-testid="scene-reply-mode-picker" className="border border-stone-800 bg-stone-900/30 p-4">
                        <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-3">
                          Who replies?
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            data-testid="scene-reply-mode-auto"
                            onClick={() => setChatReplyMode("auto")}
                            className={`p-3 border text-left transition ${chatReplyMode === "auto"
                              ? "border-rose-800 bg-rose-950/25"
                              : "border-stone-800 bg-stone-900/40 hover:border-stone-600"}`}
                          >
                            <div className="font-serif-reading text-stone-100 mb-0.5">Natural</div>
                            <div className="font-sans-ui text-[9px] uppercase tracking-[0.2em] text-stone-500">
                              AI decides who speaks
                            </div>
                          </button>
                          <button
                            type="button"
                            data-testid="scene-reply-mode-directed"
                            onClick={() => setChatReplyMode("directed")}
                            className={`p-3 border text-left transition ${chatReplyMode === "directed"
                              ? "border-rose-800 bg-rose-950/25"
                              : "border-stone-800 bg-stone-900/40 hover:border-stone-600"}`}
                          >
                            <div className="font-serif-reading text-stone-100 mb-0.5">Directed</div>
                            <div className="font-sans-ui text-[9px] uppercase tracking-[0.2em] text-stone-500">
                              You pick who speaks, in order
                            </div>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div>
              <p className="font-serif-reading text-stone-400 text-lg italic">
                {(mode === "chat" || mode === "scene")
                  ? "Two to four figures for the scene."
                  : "Choose up to five figures. You may also compose your own from your"}
                {!(mode === "chat" || mode === "scene") && (
                  <a href="/characters" className="text-rose-300 hover:text-rose-200 ml-2">private library →</a>
                )}
              </p>
              <p className="font-sans-ui text-[11px] uppercase tracking-[0.25em] text-stone-500 mt-3">
                {selectedCharIds.length} of {(mode === "chat" || mode === "scene") ? 4 : 5} selected · {controlledIds.length} {(mode === "chat" || mode === "scene") ? "player" : "under your direct control"}
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {characters.map((c) => {
                const sel = selectedCharIds.includes(c.id);
                const ctrl = controlledIds.includes(c.id);
                return (
                  <div
                    key={c.id}
                    data-testid={`char-card-${c.id}`}
                    className={`relative text-left p-6 border transition-all ${sel
                      ? "border-rose-800 bg-rose-950/20"
                      : "border-stone-800 bg-stone-900/30 hover:border-stone-600"}`}
                  >
                    <button
                      data-testid={`char-select-${c.id}`}
                      onClick={() => toggleChar(c.id)}
                      className="w-full text-left"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-serif-heading text-2xl text-stone-100">{c.name}</h3>
                        {sel && <Check className="w-5 h-5 text-rose-500" strokeWidth={1.5} />}
                      </div>
                      <p className="font-serif-reading text-stone-400 text-sm italic leading-relaxed mb-3">
                        {c.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {c.traits?.slice(0,3).map((t,i) => (
                          <span key={i} className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-stone-500 border border-stone-800 px-2 py-1">
                            {t}
                          </span>
                        ))}
                        {!c.is_predefined && <span className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-rose-400 px-2 py-1">Yours</span>}
                      </div>
                    </button>
                    {sel && (
                      <div className="mt-4 pt-4 border-t border-stone-800/60 flex items-center justify-between gap-3">
                        <div className="font-sans-ui text-[10px] uppercase tracking-[0.25em] text-stone-500">
                          {mode === "chat"
                            ? (ctrl ? "You text as them" : "They text you back")
                            : mode === "scene"
                              ? (ctrl ? "You write their lines" : "AI voices them")
                              : (ctrl ? "You speak through them" : "AI drives them")}
                        </div>
                        <button
                          data-testid={`char-control-${c.id}`}
                          onClick={(e) => toggleControl(c.id, e)}
                          className={`font-sans-ui text-[10px] uppercase tracking-[0.25em] border px-3 py-1.5 transition ${
                            ctrl
                              ? "border-rose-700 bg-rose-900/30 text-rose-200"
                              : "border-stone-700 bg-stone-900 text-stone-400 hover:text-stone-200 hover:border-stone-500"
                          }`}
                        >
                          {(mode === "chat" || mode === "scene")
                            ? (ctrl ? "I play as them" : "Play as them")
                            : (ctrl ? "I control" : "Let me control")}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 1: Theme */}
        {step === 1 && (
          <div className="space-y-8 fade-up">
            <p className="font-serif-reading text-stone-400 text-lg italic">Each theme is a tempo. Choose one &mdash; or compose your own.</p>
            <div className="grid md:grid-cols-2 gap-5">
              {themes.map((t) => {
                const sel = themeId === t.id;
                return (
                  <button
                    key={t.id}
                    data-testid={`theme-select-${t.id}`}
                    onClick={() => pickTheme(t)}
                    className={`text-left p-7 border transition-all ${sel
                      ? "border-rose-800 bg-rose-950/20"
                      : "border-stone-800 bg-stone-900/30 hover:border-stone-600"}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-serif-heading text-3xl text-stone-100">{t.name}</h3>
                      <div className="flex items-center gap-2">
                        {!t.is_predefined && <span className="font-sans-ui text-[9px] uppercase tracking-[0.2em] text-rose-400 border border-rose-900/60 px-2 py-0.5">Yours</span>}
                        {sel && <Check className="w-5 h-5 text-rose-500" strokeWidth={1.5} />}
                      </div>
                    </div>
                    <p className="font-serif-reading text-stone-400 text-base italic leading-relaxed mb-4">
                      {t.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(t.default_vibes || []).map((v, i) => (
                        <span key={i} className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-stone-500 border border-stone-800 px-2 py-1">
                          {v}
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}

              <button
                data-testid="theme-create-own"
                onClick={openThemeDialog}
                className="text-left p-7 border border-dashed border-stone-700 bg-transparent hover:border-rose-800 hover:bg-rose-950/10 transition-all flex items-center justify-center min-h-[160px]"
              >
                <div className="text-center">
                  <Plus className="w-5 h-5 text-rose-800 mx-auto mb-3" strokeWidth={1.5} />
                  <div className="font-serif-heading text-2xl text-stone-200 mb-1">Create your own</div>
                  <div className="font-sans-ui text-[10px] uppercase tracking-[0.25em] text-stone-500">
                    Compose a theme of your own making
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Vibes */}
        {step === 2 && (
          <div className="space-y-8 fade-up">
            <div>
              <p className="font-serif-reading text-stone-400 text-lg italic">
                Pick up to four tones. {themeId ? "Pre-filled from your theme — adjust to taste." : "These guide the AI's register throughout the story."}
              </p>
              <p className="font-sans-ui text-[11px] uppercase tracking-[0.25em] text-stone-500 mt-3">
                {vibes.length} of 6 chosen · optional
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {VIBE_OPTIONS.map((v) => {
                const sel = vibes.includes(v.v);
                const disabled = !sel && vibes.length >= 6;
                return (
                  <button
                    key={v.v}
                    data-testid={`vibe-${v.v}`}
                    disabled={disabled}
                    onClick={() => toggleVibe(v.v)}
                    className={`p-5 border text-left transition ${
                      sel
                        ? "border-rose-800 bg-rose-950/30"
                        : disabled
                          ? "border-stone-900 bg-stone-950 opacity-40 cursor-not-allowed"
                          : "border-stone-800 bg-stone-900/30 hover:border-stone-500"
                    }`}
                  >
                    <div className="font-serif-heading text-2xl text-stone-100 mb-1">{v.l}</div>
                    <div className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-stone-500">{v.d}</div>
                  </button>
                );
              })}
            </div>

            <div className="pt-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">
                  Add your own vibe
                </div>
                <div className="flex-1 h-px bg-stone-800/60"></div>
              </div>
              <div className="flex gap-3">
                <Input
                  data-testid="custom-vibe-input"
                  value={customVibeInput}
                  onChange={(e) => setCustomVibeInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomVibe(); } }}
                  placeholder="e.g. forbidden, cinematic, reckless, sacred…"
                  className="bg-stone-900 border-stone-800 rounded-none h-12 text-stone-100 font-serif-reading focus:ring-rose-900 flex-1"
                />
                <Button
                  data-testid="custom-vibe-add-btn"
                  type="button"
                  onClick={addCustomVibe}
                  disabled={!customVibeInput.trim() || vibes.length >= 6}
                  className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-12 px-5 font-sans-ui text-xs uppercase tracking-[0.25em]"
                >
                  Add
                </Button>
              </div>
              {vibes.filter(v => !VIBE_OPTIONS.find(o => o.v === v)).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {vibes.filter(v => !VIBE_OPTIONS.find(o => o.v === v)).map(v => (
                    <span key={v} data-testid={`custom-vibe-chip-${v}`}
                      className="inline-flex items-center gap-2 font-sans-ui text-[11px] uppercase tracking-[0.2em] text-rose-200 border border-rose-900/60 bg-rose-950/20 px-3 py-1.5">
                      {v}
                      <button onClick={() => setVibes(vibes.filter(x => x !== v))} className="text-rose-400 hover:text-rose-200">
                        <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Title & Tone */}
        {step === 3 && (
          <div className="space-y-8 fade-up max-w-2xl">
            <div>
              <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Title</Label>
              <Input
                data-testid="story-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-3 bg-stone-900 border-stone-800 rounded-none h-14 text-stone-100 font-serif-heading text-2xl focus:ring-rose-900"
                placeholder="A name for your story"
              />
            </div>
            <div>
              <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Intensity</Label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
                {[
                  {v:"story", l:"Story Only", d:"Pure narrative, no romance"},
                  {v:"sweet", l:"Sweet", d:"Story & romance, no heat"},
                  {v:"mild", l:"Suggestive", d:"Tasteful, implied"},
                  {v:"steamy", l:"Steamy", d:"Sensual, charged"},
                  {v:"explicit", l:"Explicit", d:"Unflinching"},
                  {v:"kink", l:"Kink", d:"BDSM, power play"},
                ].map(o => (
                  <button
                    key={o.v}
                    data-testid={`intensity-${o.v}`}
                    type="button"
                    onClick={() => setIntensity(o.v)}
                    className={`p-5 border text-center transition ${intensity === o.v
                      ? "border-rose-800 bg-rose-950/20"
                      : "border-stone-800 bg-stone-900/30 hover:border-stone-600"}`}
                  >
                    <div className="font-serif-heading text-xl text-stone-100">{o.l}</div>
                    <div className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-stone-500 mt-2">{o.d}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">
                Genre <span className="text-stone-600 normal-case tracking-normal ml-1">(steers the AI&apos;s narrative lane)</span>
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 mt-3">
                {[
                  {v:"open",     l:"Open",     d:"No constraint"},
                  {v:"romance",  l:"Romance",  d:"Feelings forward"},
                  {v:"mystery",  l:"Mystery",  d:"Driven by a question"},
                  {v:"thriller", l:"Thriller", d:"Pressure & threat"},
                  {v:"drama",    l:"Drama",    d:"Internal conflict"},
                  {v:"scifi",    l:"Sci-Fi",   d:"World rules matter"},
                  {v:"fantasy",  l:"Fantasy",  d:"Built-world magic"},
                  {v:"horror",   l:"Horror",   d:"Dread accumulates"},
                  {v:"literary", l:"Literary", d:"Voice over plot"},
                  {v:"comedy",   l:"Comedy",   d:"Wry, character-rooted"},
                ].map(o => (
                  <button
                    key={o.v}
                    type="button"
                    data-testid={`genre-${o.v}`}
                    onClick={() => setGenre(o.v)}
                    className={`p-3 border text-center transition ${genre === o.v
                      ? "border-rose-800 bg-rose-950/20"
                      : "border-stone-800 bg-stone-900/30 hover:border-stone-600"}`}
                  >
                    <div className="font-serif-heading text-base text-stone-100">{o.l}</div>
                    <div className="font-sans-ui text-[9px] uppercase tracking-[0.18em] text-stone-500 mt-1.5">{o.d}</div>
                  </button>
                ))}
              </div>
              <div className="font-serif-reading italic text-stone-600 text-xs mt-2 leading-relaxed">
                Pick &ldquo;Open&rdquo; if you want the AI to follow vibes without genre steering.
              </div>
            </div>
            <div>
              <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Setup note <span className="text-stone-600 normal-case tracking-normal ml-1">(optional, sets the opening)</span></Label>
              <Textarea
                data-testid="story-setup-input"
                value={setupNote}
                onChange={(e) => setSetupNote(e.target.value)}
                className="mt-3 bg-stone-900 border-stone-800 rounded-none text-stone-100 font-serif-reading focus:ring-rose-900 min-h-[120px]"
                placeholder="They meet in a train station in Lisbon, neither expecting…"
              />
            </div>
            <div>
              <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">
                Do not list <span className="text-stone-600 normal-case tracking-normal ml-1">(optional, hard exclusions)</span>
              </Label>
              <Textarea
                data-testid="story-avoid-input"
                value={avoidNote}
                onChange={(e) => setAvoidNote(e.target.value)}
                className="mt-3 bg-stone-900 border-stone-800 rounded-none text-stone-100 font-serif-reading focus:ring-rose-900 min-h-[110px]"
                placeholder={"e.g. No 'God', 'Christ', 'Jesus' as exclamations. No pet names like 'baby', 'honey'. No infidelity. No smoking."}
              />
              <div className="font-serif-reading italic text-stone-600 text-xs mt-2 leading-relaxed">
                Be <span className="text-rose-400">specific</span> &mdash; the AI reads these literally.
                &ldquo;No blasphemy&rdquo; works best when you list the actual words to avoid: &ldquo;No &lsquo;God&rsquo;, &lsquo;Oh God&rsquo;, &lsquo;Christ&rsquo;, &lsquo;Jesus&rsquo; as exclamations.&rdquo;
              </div>
            </div>

            <button
              type="button"
              data-testid="no-emotional-arc-toggle"
              onClick={() => setNoEmotionalArc(v => !v)}
              className={`w-full flex items-start gap-3 p-4 border transition text-left ${noEmotionalArc
                ? "border-rose-800 bg-rose-950/25"
                : "border-stone-800 bg-stone-900/30 hover:border-stone-600"}`}
            >
              <div className={`mt-0.5 w-4 h-4 border flex items-center justify-center shrink-0 ${noEmotionalArc ? "bg-rose-900 border-rose-800" : "border-stone-600 bg-stone-950"}`}>
                {noEmotionalArc && <Check className="w-3 h-3 text-rose-50" strokeWidth={2.5} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-sans-ui text-[11px] uppercase tracking-[0.25em] text-stone-300">
                  Keep it physical &mdash; no emotional arc
                </div>
                <div className="font-serif-reading italic text-stone-500 text-xs mt-1 leading-relaxed">
                  {noEmotionalArc
                    ? "The AI will NOT manufacture feelings or bonding. Lust, adversarial tension, casual fun — no meaningful stares, no falling in love."
                    : "Stops the AI from forcing characters to develop feelings. Good for pure hookups, rivalries, tense play."}
                </div>
              </div>
            </button>
            <SetupDirectionSection
              mode={mode}
              aiChars={selectedChars.filter(c => !controlledIds.includes(c.id))}
              directions={directions}
              setDirections={setDirections}
              walkOns={walkOns}
              setWalkOns={setWalkOns}
            />
          </div>
        )}

        {/* Step 4: Review */}
        {step === 4 && (
          <div className="fade-up max-w-2xl">
            <p className="font-serif-reading text-stone-400 text-lg italic mb-10">
              Everything is set. Inkwell will open the story on the next page &mdash; the prose will form as you watch.
            </p>
            <div className="border border-stone-800 p-8 space-y-5">
              <div>
                <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-2">Title</div>
                <div className="font-serif-heading text-3xl text-stone-100">{title}</div>
              </div>
              <div className="hairline"></div>
              <div>
                <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-2">Format</div>
                <div className="font-serif-reading text-stone-300 capitalize">
                  {mode === "chat" ? "Texting thread" : mode === "scene" ? "Scene" : "Prose story"}
                  {mode === "scene" && !walkOns && <span className="text-stone-500"> · walk-ons off</span>}
                  {Object.keys(directions).some(cid => selectedCharIds.includes(cid) && !controlledIds.includes(cid)) && (
                    <span className="text-stone-500" data-testid="review-direction-names"> · direction: {selectedChars.filter(c => directions[c.id] && !controlledIds.includes(c.id)).map(c => c.name.split(" ")[0]).join(", ")}</span>
                  )}
                </div>
              </div>
              <div className="hairline"></div>
              <div>
                <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-2">Cast</div>
                <div className="font-serif-reading text-stone-300 space-y-1">
                  {selectedChars.map((c) => (
                    <div key={c.id} className="flex items-center gap-3">
                      <UserIcon className="w-3 h-3 text-stone-600" strokeWidth={1.5} />
                      <span>{c.name}</span>
                      {controlledIds.includes(c.id) && (
                        <span className="font-sans-ui text-[10px] uppercase tracking-[0.2em] text-rose-400 border border-rose-900/60 px-2 py-0.5">
                          I control
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="hairline"></div>
              <div>
                <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-2">Theme · Intensity</div>
                <div className="font-serif-reading text-stone-300">
                  {selectedTheme?.name} · <span className="capitalize">{intensity}</span>
                </div>
              </div>
              {vibes.length > 0 && (
                <>
                  <div className="hairline"></div>
                  <div>
                    <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-2">Vibes</div>
                    <div className="flex flex-wrap gap-2">
                      {vibes.map(v => (
                        <span key={v} className="font-sans-ui text-[11px] uppercase tracking-[0.2em] text-rose-200 border border-rose-900/60 px-3 py-1.5">
                          {VIBE_OPTIONS.find(x => x.v === v)?.l || v}
                        </span>
                      ))}
                    </div>
                  </div>
                </>
              )}
              {avoidNote.trim() && (
                <>
                  <div className="hairline"></div>
                  <div>
                    <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-2">Do not list</div>
                    <div className="font-serif-reading italic text-stone-400 text-sm leading-relaxed whitespace-pre-wrap">
                      {avoidNote}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Footer nav */}
        <div className="flex items-center justify-between mt-14 gap-3 flex-wrap">
          <Button
            variant="ghost"
            data-testid="step-back-btn"
            disabled={step === 0}
            onClick={() => setStep(s => Math.max(0, s - 1))}
            className="rounded-none h-12 px-4 sm:px-5 text-stone-400 hover:text-stone-100 hover:bg-transparent font-sans-ui text-xs uppercase tracking-[0.25em] disabled:opacity-30"
          >
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> Back
          </Button>
          <div className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-600 order-last sm:order-none w-full sm:w-auto text-center">
            {step+1} of {STEPS.length}
          </div>
          {step < STEPS.length - 1 ? (
            <Button
              data-testid="step-next-btn"
              disabled={!canAdvance()}
              onClick={() => setStep(s => s + 1)}
              className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-12 px-5 sm:px-6 font-sans-ui text-xs uppercase tracking-[0.25em]"
            >
              Continue <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          ) : (
            <Button
              data-testid="generate-story-btn"
              disabled={generating}
              onClick={submit}
              className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-12 px-5 sm:px-8 font-sans-ui text-xs uppercase tracking-[0.25em]"
            >
              <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {generating ? "Opening…" : "Begin the story"} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          )}
        </div>
      </div>

      {/* Theme creator dialog */}
      <Dialog open={themeDialogOpen} onOpenChange={setThemeDialogOpen}>
        <DialogContent className="bg-stone-950 border-stone-800 rounded-none max-w-xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif-heading text-3xl font-light text-stone-100">
              Compose a theme
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div>
              <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">
                Suggestions <span className="text-stone-600 normal-case tracking-normal ml-1">(tap to start from one)</span>
              </Label>
              <div className="flex flex-wrap gap-2 mt-3">
                {THEME_SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`theme-suggestion-${i}`}
                    onClick={() => applySuggestion(s)}
                    className="font-serif-heading italic text-sm text-stone-300 border border-stone-800 bg-stone-900 hover:border-rose-800 hover:text-rose-200 px-3 py-1.5 transition"
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Name</Label>
              <Input
                data-testid="theme-create-name"
                value={themeForm.name}
                onChange={(e) => setThemeForm({ ...themeForm, name: e.target.value })}
                className="mt-2 bg-stone-900 border-stone-800 rounded-none h-12 text-stone-100 font-serif-reading"
                placeholder="e.g. The Rainstorm"
              />
            </div>
            <div>
              <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">Description</Label>
              <Textarea
                data-testid="theme-create-desc"
                value={themeForm.description}
                onChange={(e) => setThemeForm({ ...themeForm, description: e.target.value })}
                className="mt-2 bg-stone-900 border-stone-800 rounded-none text-stone-100 font-serif-reading min-h-[90px]"
                placeholder="A couple of sentences that set the scene and the stakes."
              />
            </div>
            <div>
              <Label className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-stone-500">
                Default vibes <span className="text-stone-600 normal-case tracking-normal ml-1">(up to 6, optional)</span>
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
                {VIBE_OPTIONS.map((v) => {
                  const sel = (themeForm.default_vibes || []).includes(v.v);
                  const disabled = !sel && (themeForm.default_vibes || []).length >= 6;
                  return (
                    <button
                      key={v.v}
                      type="button"
                      data-testid={`theme-create-vibe-${v.v}`}
                      disabled={disabled}
                      onClick={() => toggleThemeFormVibe(v.v)}
                      className={`p-2 border font-sans-ui text-[11px] uppercase tracking-[0.2em] transition ${
                        sel
                          ? "border-rose-800 bg-rose-950/30 text-rose-200"
                          : disabled
                            ? "border-stone-900 bg-stone-950 text-stone-600 opacity-50"
                            : "border-stone-800 bg-stone-900 text-stone-400 hover:text-stone-100"
                      }`}
                    >{v.l}</button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="theme-create-save"
              onClick={saveCustomTheme}
              disabled={savingTheme}
              className="bg-rose-900 hover:bg-rose-800 text-rose-50 rounded-none h-11 font-sans-ui text-xs uppercase tracking-[0.25em]"
            >
              {savingTheme ? "Saving…" : "Save & select"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateStory;
